import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AIAnalysisPanel = ({ patientData, onGenerateAnalysis }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleGenerateAnalysis = async () => {
    setIsLoading(true);
    setProgress(0);
    
    // Simulate AI processing with progress updates
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + Math.random() * 15;
      });
    }, 200);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Mock AI analysis result
      const mockAnalysis = {
        summary: `Based on 30-day analysis of ${patientData?.name}'s neurological health data, several key patterns emerge that warrant clinical attention.\n\nSymptom severity shows a concerning upward trend over the past two weeks, with average severity increasing from 4.2 to 6.8. The most frequent symptoms are headaches (45% of logs) and cognitive fog (32% of logs), typically occurring in the afternoon hours.\n\nCognitive performance data reveals declining reaction times, particularly in memory-based tasks, with scores dropping 15% compared to baseline measurements. This correlates strongly with increased symptom severity (r=0.78).`,
        keyInsights: [
          {
            title: 'Symptom Pattern Recognition',description: 'Headaches consistently peak between 2-4 PM, suggesting potential circadian rhythm involvement',severity: 'medium',icon: 'TrendingUp'
          },
          {
            title: 'Cognitive Decline Correlation',description: 'Strong negative correlation between symptom severity and cognitive test performance',severity: 'high',icon: 'AlertTriangle'
          },
          {
            title: 'Treatment Response',description: 'Symptom logs show 23% improvement on days following cognitive game sessions',severity: 'low',icon: 'CheckCircle'
          }
        ],
        recommendations: [
          'Consider adjusting medication timing to address afternoon symptom peaks','Implement structured cognitive training program with daily 15-minute sessions','Monitor sleep patterns as potential contributing factor to cognitive decline','Schedule follow-up neurological assessment within 2 weeks'
        ],
        riskFactors: [
          { factor: 'Symptom Progression', level: 'High', trend: 'Increasing' },
          { factor: 'Cognitive Decline', level: 'Medium', trend: 'Stable' },
          { factor: 'Treatment Adherence', level: 'Low', trend: 'Improving' }
        ],
        generatedAt: new Date()?.toISOString()
      };

      setProgress(100);
      setTimeout(() => {
        setAnalysis(mockAnalysis);
        setIsLoading(false);
        setIsExpanded(true);
        onGenerateAnalysis?.(mockAnalysis);
      }, 500);
      
    } catch (error) {
      setIsLoading(false);
      setProgress(0);
      console.error('AI Analysis failed:', error);
    }
    
    clearInterval(progressInterval);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const exportAnalysis = () => {
    if (!analysis) return;
    
    const exportData = {
      patient: patientData?.name,
      analysisDate: new Date(analysis.generatedAt)?.toLocaleDateString(),
      summary: analysis?.summary,
      insights: analysis?.keyInsights,
      recommendations: analysis?.recommendations,
      riskFactors: analysis?.riskFactors
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${patientData?.name}_AI_Analysis_${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">AI Health Analysis</h2>
          <p className="text-sm text-gray-600 mt-1">
            Gemini-powered analysis of patient health trends and correlations
          </p>
        </div>
        
        {analysis && (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportAnalysis}
              iconName="Download"
            >
              Export Report
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </Button>
          </div>
        )}
      </div>
      {/* Generate Analysis Button */}
      {!analysis && !isLoading && (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Brain" size={32} color="#2563eb" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Generate AI Analysis</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Analyze {patientData?.name}'s health data to identify patterns, correlations, and provide clinical insights.
          </p>
          <Button
            variant="default"
            onClick={handleGenerateAnalysis}
            iconName="Sparkles"
            className="px-8"
          >
            Generate AI Summary
          </Button>
        </div>
      )}
      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Icon name="Brain" size={32} color="#2563eb" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Analyzing Patient Data</h3>
          <p className="text-gray-600 mb-4">Processing neurological health patterns...</p>
          
          {/* Progress Bar */}
          <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-2 mb-4">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm text-gray-500">{Math.round(progress)}% complete</p>
        </div>
      )}
      {/* Analysis Results */}
      {analysis && (
        <div className="space-y-6">
          {/* Summary */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Executive Summary</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {analysis?.summary}
              </p>
            </div>
          </div>

          {isExpanded && (
            <>
              {/* Key Insights */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Insights</h3>
                <div className="grid gap-4">
                  {analysis?.keyInsights?.map((insight, index) => (
                    <div key={index} className={`border rounded-lg p-4 ${getSeverityColor(insight?.severity)}`}>
                      <div className="flex items-start space-x-3">
                        <Icon name={insight?.icon} size={20} />
                        <div className="flex-1">
                          <h4 className="font-medium mb-1">{insight?.title}</h4>
                          <p className="text-sm opacity-90">{insight?.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Risk Factors */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Risk Assessment</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 text-sm font-medium text-gray-700">Risk Factor</th>
                        <th className="text-left py-2 text-sm font-medium text-gray-700">Level</th>
                        <th className="text-left py-2 text-sm font-medium text-gray-700">Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis?.riskFactors?.map((risk, index) => (
                        <tr key={index} className="border-b border-gray-100">
                          <td className="py-2 text-sm text-gray-900">{risk?.factor}</td>
                          <td className="py-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(risk?.level?.toLowerCase())}`}>
                              {risk?.level}
                            </span>
                          </td>
                          <td className="py-2 text-sm text-gray-600">{risk?.trend}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recommendations */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Clinical Recommendations</h3>
                <div className="space-y-2">
                  {analysis?.recommendations?.map((rec, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                      <Icon name="Lightbulb" size={16} color="#2563eb" className="mt-0.5" />
                      <p className="text-sm text-gray-700">{rec}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Generated Timestamp */}
              <div className="text-xs text-gray-500 pt-4 border-t border-gray-200">
                Analysis generated on {new Date(analysis.generatedAt)?.toLocaleString()} using Gemini AI
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AIAnalysisPanel;